import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, type Aenderung } from '../api'
import { Tipp } from './Hinweis'

const datum = (iso: string) => new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
}).format(new Date(`${iso}T12:00:00`))

export function Aenderungsverlauf() {
  const [eintraege, setEintraege] = useState<Aenderung[]>([])
  const [offen, setOffen] = useState<number | null>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const vorher = useRef<HTMLElement | null>(null)

  useEffect(() => {
    api.aenderungen().then(setEintraege).catch(() => setEintraege([]))
  }, [])

  useEffect(() => {
    if (offen === null) return
    vorher.current = document.activeElement as HTMLElement | null
    const fenster = dialog.current
    fenster?.querySelector<HTMLElement>('button')?.focus()

    const taste = (ereignis: KeyboardEvent) => {
      if (ereignis.key === 'Escape') {
        ereignis.preventDefault()
        setOffen(null)
        return
      }
      if (ereignis.key !== 'Tab' || !fenster) return
      const knoepfe = Array.from(fenster.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), '
        + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      if (!knoepfe.length) return
      const erster = knoepfe[0]
      const letzter = knoepfe[knoepfe.length - 1]
      if (ereignis.shiftKey && document.activeElement === erster) {
        ereignis.preventDefault(); letzter.focus()
      } else if (!ereignis.shiftKey && document.activeElement === letzter) {
        ereignis.preventDefault(); erster.focus()
      }
    }
    document.addEventListener('keydown', taste)
    return () => {
      document.removeEventListener('keydown', taste)
      vorher.current?.focus()
    }
  }, [offen])

  if (!eintraege.length) return null
  const ausgewaehlt = offen === null ? null : eintraege[offen]
  const oeffnen = (index: number) => setOffen(index)

  return (
    <>
      <section className="aenderungen-kurz" aria-labelledby="aenderungen-titel">
        <h2 id="aenderungen-titel">Neuigkeiten</h2>
        {eintraege.slice(0, 3).map((eintrag, index) => (
          <button key={`${eintrag.datum}-${eintrag.titel}`} onClick={() => oeffnen(index)}>
            <span className={`aenderung-punkt art-${eintrag.art.toLowerCase()}`} aria-hidden="true" />
            <span className="aenderung-kurztitel">{eintrag.titel}</span>
            <time dateTime={eintrag.datum}>{datum(eintrag.datum)}</time>
          </button>
        ))}
        <button className="aenderungen-alle" onClick={() => oeffnen(0)}>
          Alle Änderungen
        </button>
      </section>

      {ausgewaehlt && createPortal((
        <div className="aenderungen-hintergrund"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setOffen(null) }}>
          <div ref={dialog} className="aenderungen-dialog" role="dialog" aria-modal="true"
               aria-labelledby="aenderung-dialog-titel">
            <div className="aenderungen-detail">
              <Tipp text="Schließen">
                <button className="aenderungen-schliessen" aria-label="Fenster schließen"
                        onClick={() => setOffen(null)}>×</button>
              </Tipp>
              <div className="aenderung-meta">
                <span className={`aenderung-art art-${ausgewaehlt.art.toLowerCase()}`}>
                  {ausgewaehlt.art}
                </span>
                <time dateTime={ausgewaehlt.datum}>{datum(ausgewaehlt.datum)}</time>
              </div>
              <h2 id="aenderung-dialog-titel">{ausgewaehlt.titel}</h2>
              <p className="aenderung-bereich">{ausgewaehlt.bereich}</p>
              <div className="aenderung-text">
                {ausgewaehlt.text.split(/\n\n+/).map((absatz, index) => (
                  <p key={index}>{absatz}</p>
                ))}
              </div>
            </div>
            <nav className="aenderungen-liste" aria-label="Alle Änderungen">
              <h3>Alle Änderungen</h3>
              {eintraege.map((eintrag, index) => (
                <button key={`${eintrag.datum}-${eintrag.titel}`}
                        aria-current={offen === index ? 'true' : undefined}
                        onClick={() => setOffen(index)}>
                  <span className={`aenderung-punkt art-${eintrag.art.toLowerCase()}`}
                        aria-hidden="true" />
                  <span>{eintrag.titel}</span>
                  <time dateTime={eintrag.datum}>{datum(eintrag.datum)}</time>
                </button>
              ))}
            </nav>
          </div>
        </div>
      ), document.body)}
    </>
  )
}
